import { Component } from '@angular/core';
import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CardComponent } from '../card/card.component';
import { BackButtonComponent } from '../back-button/back-button.component';
import { LogoImagesComponent } from '../logo-images/logo-images.component';
import { PasswordToggleComponent } from '../password-toggle/password-toggle.component';

@Component({
  selector: 'app-reset-password-form',
  standalone: true,
  imports: [
    MatButtonModule,
    RouterModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    CardComponent,
    BackButtonComponent,
    LogoImagesComponent,
    PasswordToggleComponent
  ],
  templateUrl: './reset-password-form.component.html',
  styleUrls: ['./reset-password-form.component.scss']
})
export class ResetPasswordFormComponent {
  resetPasswordForm!: FormGroup<{
    newPassword: FormControl<string | null>;
    newPasswordRepeated: FormControl<string | null>;
  }>;

  isLoading = false;
  formState: null | boolean = null;
  resetCodeParam: string | null = null;

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,}$/;
    const hasParam = this.route.snapshot.queryParamMap.has('resetCode');
    this.resetCodeParam = hasParam ? this.route.snapshot.queryParamMap.get('resetCode') : null;

    this.resetPasswordForm = this.formBuilder.group(
      {
        newPassword: ['', [Validators.required, Validators.minLength(6), Validators.pattern(passwordRegex)]],
        newPasswordRepeated: ['', [Validators.required, Validators.minLength(6), Validators.pattern(passwordRegex)]]
      },
      { validators: this.passwordsMatchValidator }
    );
  }

  async onSubmit() {
    if (this.resetPasswordForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.formState = null;
    this.resetPasswordForm.disable();

    try {
      await lastValueFrom(
        this.authService.resetPassword({
          resetCode: this.resetCodeParam ?? '',
          newPassword: this.resetPasswordForm.value.newPassword ?? ''
        })
      );
      this.formState = true;
    } catch (error) {
      this.formState = false;
    } finally {
      this.isLoading = false;
      this.resetPasswordForm.enable();
    }
  }

  private passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword')?.value;
    const confirm = group.get('newPasswordRepeated')?.value;
    return password === confirm ? null : { passwordsMismatch: true };
  }
}
